import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Loading, MessagePlugin, Space, Tag } from 'tdesign-react';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DownloadIcon,
  FileIcon,
  RollbackIcon,
} from 'tdesign-icons-react';
import {
  DISPATCH_STATUS_LABEL,
  DISPATCH_STATUS_THEME,
  DispatchStatus,
  SUB_CATEGORY_LABEL,
  confirmCompleteCommand,
  fetchCommandCompleteDownloadUrl,
  fetchCommandDetail,
  fetchCommandDownloadUrl,
  rejectCompleteCommand,
  type CommandRecord,
} from '@/services/command';
import { formatDateTime, formatDateOnly } from '@/utils/datetime';
import './CommandDetail.css';

const formatSize = (bytes: number | null | undefined) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 后台「指令详情」页
 *
 * 路由：`/commands/detail?id=<id>`
 * 入口：CommandList 操作列「详情」按钮 → navigate 只带 id，进入后总是通过接口拉取。
 *
 * 数据来源：始终调 `/command/detail?id=xxx`。
 *  - 不使用 location.state，也不做任何页面级缓存（下发/完成状态可能实时变化，
 *    列表页快照跟服务端很快就不一致）。
 *  - 每次挂载 / id 变化 / 刷新，都重新发起请求。
 *
 * 展示风格参考前台 CompletePage 的卡片布局，同时提供两类附件的临时签名 URL 下载入口：
 *  - 指令原附件（fileKey / fileName）
 *  - 完成回执附件（completeFileKey / completeFileName），仅 need_reply=1 且已完成时存在
 */
const CommandDetail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const id = Number(params.get('id') || 0);
  const paramsOk = id > 0;

  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<CommandRecord | null>(null);
  const [errMsg, setErrMsg] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  /**
   * 拉取指令详情
   *  - 首次挂载 / id 变化：进入 loading 骨架
   *  - 「确认完成」等操作后局部刷新：silent=true，只更新 record，不闪 loading
   */
  const loadDetail = useCallback(
    async (silent = false) => {
      if (!paramsOk) {
        setErrMsg('参数错误：缺少指令 id');
        setRecord(null);
        return;
      }
      if (!silent) {
        setLoading(true);
        setErrMsg('');
        setRecord(null);
      }
      try {
        const found = await fetchCommandDetail(id);
        setRecord(found);
        if (silent) setErrMsg('');
      } catch (err) {
        // detail 接口在指令不存在时会抛错 → 拦截器统一 toast，这里只更新页面错误态
        if (!silent) {
          setErrMsg((err as Error).message || '指令不存在或已删除');
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, paramsOk],
  );

  useEffect(() => {
    // 数据可能实时变化（下发/完成状态、完成回执附件），每次都强制走接口
    let cancelled = false;
    if (!paramsOk) {
      setErrMsg('参数错误：缺少指令 id');
      setRecord(null);
      return;
    }
    setLoading(true);
    setErrMsg('');
    setRecord(null);
    fetchCommandDetail(id)
      .then((found) => {
        if (cancelled) return;
        setRecord(found);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setErrMsg(err.message || '指令不存在或已删除');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, paramsOk]);

  const handleConfirmComplete = async () => {
    if (!record) return;
    setConfirming(true);
    try {
      await confirmCompleteCommand(record.id);
      MessagePlugin.success('已确认完成');
      await loadDetail(true);
    } catch {
      /* 错误提示由拦截器统一处理 */
    } finally {
      setConfirming(false);
    }
  };

  /**
   * 退回到「已下发」：
   *  - 只改数据库状态（PendingConfirm → Dispatched），保留历史完成信息
   *  - 不向企微群发任何卡片；处理人可从之前的群聊卡片链接继续提交
   */
  const handleRejectComplete = async () => {
    if (!record) return;
    setRejecting(true);
    try {
      await rejectCompleteCommand(record.id);
      MessagePlugin.success('已退回，指令回到「已下发」状态');
      await loadDetail(true);
    } catch {
      /* 错误提示由拦截器统一处理 */
    } finally {
      setRejecting(false);
    }
  };

  const handleDownloadOrigin = async () => {
    if (!record?.fileKey) return;
    try {
      const res = await fetchCommandDownloadUrl(record.fileKey);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch {
      /* 错误提示由拦截器统一处理 */
    }
  };

  const handleDownloadComplete = async () => {
    if (!record?.completeFileKey) return;
    try {
      const res = await fetchCommandCompleteDownloadUrl(record.completeFileKey);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch {
      MessagePlugin.error('回执附件下载失败');
    }
  };

  const detailRows = useMemo(() => {
    if (!record) return [] as { label: string; value: React.ReactNode }[];
    const rows: { label: string; value: React.ReactNode }[] = [
      { label: '指令 ID', value: `#${record.id}` },
      {
        label: '指令分类',
        value: SUB_CATEGORY_LABEL[record.subCategory] ?? record.subCategory,
      },
      {
        // 指令内容：不管是否存在都显示字段（空则占位 "-"）
        label: '指令内容',
        value: record.content ? (
          <div className="command-detail-content">{record.content}</div>
        ) : (
          '-'
        ),
      },
      {
        // 指令附件：不管是否存在都显示字段
        // 有文件 -> 走 fetchCommandDownloadUrl → 临时签名 URL 下载；无文件 -> 占位 "-"
        label: '指令附件',
        value: record.fileName ? (
          <Button
            theme="primary"
            variant="text"
            icon={<DownloadIcon />}
            onClick={handleDownloadOrigin}
          >
            <FileIcon />
            <span style={{ marginLeft: 4 }}>{record.fileName}</span>
            {record.fileSize != null ? (
              <span className="command-detail-filesize">· {formatSize(record.fileSize)}</span>
            ) : null}
          </Button>
        ) : (
          '-'
        ),
      },
      {
        label: '是否需反馈',
        value:
          record.needReply === 1 ? (
            <Tag theme="warning" variant="light">
              需反馈
            </Tag>
          ) : (
            <Tag variant="light">无需反馈</Tag>
          ),
      },
      {
        label: '反馈日期',
        // 仅在需回复且填写了截止日期时才有语义；否则展示 '-'。
        // 走 formatDateOnly 与「下发时间」保持相同风格（YYYY/M/D）。
        value:
          record.needReply === 1 && record.deadlineDate
            ? formatDateOnly(record.deadlineDate)
            : '-',
      },
      {
        // 完成说明：need_reply=1 且已完成时可能非空；与「完成回执」附件二选一必填。
        // 无论是否为空，始终显示该行，位置固定在「完成回执」前面，便于快速看到文本内容。
        // 复用 command-detail-content 样式以支持长文本换行 / 保留空白。
        label: '完成说明',
        value: record.completeText ? (
          <div className="command-detail-content">{record.completeText}</div>
        ) : (
          '-'
        ),
      },
      {
        // 完成回执附件：不管是否存在都显示字段
        // 有回执 -> 走 fetchCommandCompleteDownloadUrl → 临时签名 URL 下载；无则占位 "-"
        label: '完成回执',
        value: record.completeFileName ? (
          <Button
            theme="primary"
            variant="text"
            icon={<DownloadIcon />}
            onClick={handleDownloadComplete}
          >
            <FileIcon />
            <span style={{ marginLeft: 4 }}>{record.completeFileName}</span>
            {record.completeFileSize != null ? (
              <span className="command-detail-filesize">
                · {formatSize(record.completeFileSize)}
              </span>
            ) : null}
          </Button>
        ) : (
          '-'
        ),
      },
      {
        label: '状态',
        value: (
          <Tag
            theme={DISPATCH_STATUS_THEME[record.dispatchStatus] ?? 'default'}
            variant="light"
          >
            {DISPATCH_STATUS_LABEL[record.dispatchStatus] ?? record.dispatchStatus}
          </Tag>
        ),
      },
      {
        label: '提交人',
        // 参考列表页展示约定：反查到 organization 时以「姓名（单位）」形式追加展示；
        // 未反查到（如太湖 staffname / 老账号未填 organization）不显示括号，
        // 避免出现"张三（未知）"这种误导性文案。
        value: (() => {
          const name = record.submitterName || record.createdBy || record.submitter || '-';
          if (name === '-' || !record.submitterOrganization) return name;
          return `${name}（${record.submitterOrganization}）`;
        })(),
      },
      { label: '提交时间', value: formatDateTime(record.createdAt) },
      { label: '下发人', value: record.dispatchedBy || '-' },
      { label: '下发时间', value: formatDateTime(record.dispatchedAt) },
      { label: '完成人', value: record.completedBy || '-' },
      { label: '完成时间', value: formatDateTime(record.completedAt) },
    ];

    return rows;
    // handleDownloadOrigin / handleDownloadComplete 为纯闭包函数，不做依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  return (
    <div className="command-detail-page">
      <div className="command-detail-toolbar">
        <Space>
          <Button
            variant="text"
            icon={<ArrowLeftIcon />}
            onClick={() => navigate('/commands')}
          >
            返回列表
          </Button>
          {record && record.dispatchStatus === DispatchStatus.PendingConfirm ? (
            <>
              <Button
                theme="primary"
                icon={<CheckCircleIcon />}
                loading={confirming}
                onClick={handleConfirmComplete}
              >
                确认完成
              </Button>
              <Button
                theme="warning"
                variant="outline"
                icon={<RollbackIcon />}
                loading={rejecting}
                onClick={handleRejectComplete}
              >
                退回
              </Button>
            </>
          ) : null}
        </Space>
      </div>

      <div className="command-detail-container">
        <div className="command-detail-header">
          <div className="command-detail-title">指令详情</div>
          <div className="command-detail-subtitle">查看指令基本信息与相关附件</div>
        </div>

        {loading ? (
          <div className="command-detail-loading">
            <Loading text="加载中..." />
          </div>
        ) : null}

        {!loading && errMsg ? (
          <div className="command-detail-error">
            <div className="command-detail-error-icon">⚠️</div>
            <div className="command-detail-error-msg">{errMsg}</div>
          </div>
        ) : null}

        {!loading && !errMsg && record ? (
          <ul className="command-detail-list">
            {detailRows.map((row) => (
              <li key={row.label} className="command-detail-row">
                <span className="command-detail-label">{row.label}</span>
                <span className="command-detail-value">{row.value}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default CommandDetail;
